import { DoneInvokeEvent, assign, createMachine } from '@monofront/xstate-v4';
import { CHECKOUT_EXTERNAL_SERVICES } from '..';
import {
  assignCart,
  assignError,
  assignProducts,
  assignUserReady,
} from './actions';
import { CHECKOUT_TAGS_LOADING } from '../tags';
import {
  areProductsMissing,
  isCartMissing,
  isCheckoutCartIdMissing,
  isUserNotReady,
} from './guards';
import { CHECKOUT_CART_STATES } from './states';
import {
  CheckoutCartMachineContext,
  CheckoutCartMachineEvents,
  GetCheckoutCartDoneEvent,
  GetCheckoutCartErrorEvent,
} from './types';
import { CHECKOUT_CART_MACHINE_ID } from './id';
import { CHECKOUT_CART_TRANSITIONS } from './transitions';

export const checkoutCartMachine = createMachine({
  id: CHECKOUT_CART_MACHINE_ID,
  initial: CHECKOUT_CART_STATES.INITIAL,
  predictableActionArguments: true,
  schema: {
    events: {} as CheckoutCartMachineEvents,
    context: {} as CheckoutCartMachineContext,
  },
  context: {
    isUserReady: false,
    checkoutCartId: null,
    checkoutCart: null,
    products: null,
    errors: null,
  },
  states: {
    [CHECKOUT_CART_STATES.INITIAL]: {
      always: [
        {
          target: CHECKOUT_CART_STATES.REFRESHING_CART,
          cond: isCartMissing,
        },
      ],
      on: {
        [CHECKOUT_CART_TRANSITIONS.START_CHECKOUT]: [
          {
            // Do nothing, and wait for USER_READY action
            actions: [assignProducts],
            cond: isUserNotReady,
          },
          {
            target: CHECKOUT_CART_STATES.SYNCING_CART,
            actions: [assignProducts],
            cond: isCheckoutCartIdMissing,
          },
          {
            target: CHECKOUT_CART_STATES.REFRESHING_CART,
            actions: [assignProducts],
          },
        ],
        [CHECKOUT_CART_TRANSITIONS.USER_READY]: [
          {
            // Do nothing, and wait for START_CHECKOUT action
            actions: [assignUserReady],
            cond: areProductsMissing,
          },
          {
            target: CHECKOUT_CART_STATES.SYNCING_CART,
            actions: [assignUserReady],
            cond: isCheckoutCartIdMissing,
          },
          {
            target: CHECKOUT_CART_STATES.REFRESHING_CART,
            actions: [assignUserReady],
          },
        ],
      },
    },
    [CHECKOUT_CART_STATES.SYNCING_CART]: {
      tags: [CHECKOUT_TAGS_LOADING],
      invoke: {
        src: CHECKOUT_EXTERNAL_SERVICES.syncCart,
        onDone: [
          {
            target: CHECKOUT_CART_STATES.CART_LOADED,
            actions: [assignCart],
            cond: (
              _,
              event: DoneInvokeEvent<any> | GetCheckoutCartDoneEvent,
            ) => {
              return (
                (!('errors' in event.data) || !event.data.errors?.length) &&
                'cart' in event.data &&
                event.data.cart != null
              );
            },
          },
          {
            target: CHECKOUT_CART_STATES.SYNC_ERROR,
            actions: [
              assign({
                errors: (_, event: GetCheckoutCartErrorEvent) =>
                  event.data.errors,
              }),
            ],
          },
        ],
        onError: {
          target: CHECKOUT_CART_STATES.SYNC_ERROR,
          actions: [assignError],
        },
      },
    },
    [CHECKOUT_CART_STATES.REFRESHING_CART]: {
      tags: [CHECKOUT_TAGS_LOADING],
      invoke: {
        src: CHECKOUT_EXTERNAL_SERVICES.refreshCart,
        onDone: [
          {
            target: CHECKOUT_CART_STATES.CART_LOADED,
            actions: [assignCart],
            cond: (
              _,
              event: DoneInvokeEvent<any> | GetCheckoutCartDoneEvent,
            ) => {
              return (
                (!('errors' in event.data) || event.data.errors.length === 0) &&
                'cart' in event.data &&
                event.data.cart != null
              );
            },
          },
          {
            target: CHECKOUT_CART_STATES.SYNC_ERROR,
            actions: [
              assign({
                errors: (_, event: GetCheckoutCartErrorEvent) =>
                  event.data.errors,
              }),
            ],
          },
        ],
        onError: {
          target: CHECKOUT_CART_STATES.SYNC_ERROR,
          actions: [assignError],
        },
      },
    },
    [CHECKOUT_CART_STATES.SYNC_ERROR]: {
      type: 'final',
      data: (context) => ({
        errors: context.errors,
      }),
    },
    [CHECKOUT_CART_STATES.CART_LOADED]: {
      type: 'final',
      data: (context) => ({
        cart: context.checkoutCart,
      }),
    },
  },
});
